import { serverMessageSchema } from "@vmeste/shared";
import type { Codec, ContentBlob, SyncDelta } from "@vmeste/shared";
import { ApiError, OfflineError, api } from "./api.js";
import {
  backoffMs,
  enqueue,
  flush,
  type Operation,
  type QueuedNotification,
  type QueuedOperation,
} from "./outbox.js";
import { applyPending } from "./pending.js";
import { primePlaintext } from "./plaintext.js";
import { codecFor, restoreSpaceCodec } from "./spacekeys.js";
import { applyDelta, emptySpaceState, type SpaceState } from "./store.js";

/**
 * Ties the pieces together: pulls deltas, drains the outbox, and listens for
 * the nudge that says something changed.
 *
 * Synced state is not persisted in v1. SPEC §5 is explicit that local data is a
 * cache rather than the source of truth, so a cold start refetches. The outbox
 * *is* persisted, because it holds changes that exist nowhere else yet — losing
 * it loses somebody's work.
 */

export type ConnectionStatus = "connecting" | "online" | "offline";

/** One catch-up per return to the screen, however many events announce it. */
const RESUME_THROTTLE_MS = 2_000;

export interface SyncSnapshot {
  /**
   * Bumped on every change, including ones no other field describes.
   *
   * `useSyncExternalStore` compares snapshots by identity and skips the render
   * when the reference is unchanged, so notifying listeners is not enough: a
   * delta arriving would fire them and change nothing else, React would bail
   * out, and the screen would keep showing the state from before. That made a
   * change from the other person invisible until something else — a status flip
   * or a local edit — happened to move one of the fields below.
   */
  version: number;
  status: ConnectionStatus;
  /** Operations still waiting to reach the server. */
  pending: number;
  /** Changes given up on, so the UI can admit they did not stick. */
  abandoned: Operation[];
}

const OUTBOX_KEY = "vmeste.outbox.v1";

function readOutbox(): QueuedOperation[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedOperation[]) : [];
  } catch {
    // Private mode, cleared storage, or a shape from an older build. Losing the
    // queue is bad, but crashing on boot is worse.
    return [];
  }
}

function writeOutbox(queue: readonly QueuedOperation[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue));
  } catch {
    /* storage unavailable — the queue lives only for this session */
  }
}

/** Send one queued operation. Mirrors the outbox operation union. */
/**
 * Hand over the notification an operation was carrying, if it had one.
 *
 * Best effort, and deliberately *after* the thing it is about. The row is
 * already saved by the time this runs, so a failure here must not fail the
 * operation — retrying the whole thing would create the row twice, and a
 * missing buzz is a far smaller loss than a duplicated message.
 *
 * The summary was written and encrypted where the person acted; this only
 * forwards the blob. The server cannot read it, which is what lets a push carry
 * real words in an encrypted space (SPEC §3, decision 3).
 */
async function announce(
  operation: Operation & { notify?: QueuedNotification },
  targetId: string,
): Promise<void> {
  if (!operation.notify) return;

  await api
    .notify(operation.spaceId, {
      type: operation.notify.type,
      targetType: operation.notify.targetType,
      targetId,
      preview: operation.notify.preview,
      ...(operation.notify.userId ? { userId: operation.notify.userId } : {}),
    })
    .catch(() => undefined);
}

/**
 * Every blob a delta carries, in one flat list.
 *
 * Written out by hand rather than walked generically: a new table with content
 * in it has to be added here, and a compiler cannot notice the omission. The
 * failure would be quiet — one kind of row showing placeholders in an encrypted
 * space while everything else worked — so the list is kept next to the type it
 * mirrors.
 */
function blobsIn(delta: SyncDelta): (ContentBlob | null | undefined)[] {
  return [
    delta.space?.content,
    ...delta.lists.map((row) => row.content),
    ...delta.items.map((row) => row.content),
    ...delta.wishes.map((row) => row.content),
    ...delta.topics.map((row) => row.content),
    ...delta.messages.map((row) => row.content),
    ...delta.events.map((row) => row.content),
    ...delta.places.map((row) => row.content),
    ...delta.placeReviews.map((row) => row.content),
    ...delta.tags.map((row) => row.content),
    ...delta.attachments.map((row) => row.content),
  ];
}

async function send(operation: Operation): Promise<void> {
  switch (operation.kind) {
    case "list.create":
      await api.createList(operation.spaceId, operation.body);
      return;
    case "list.update":
      await api.updateList(operation.spaceId, operation.listId, operation.body);
      return;
    case "list.delete":
      await api.deleteList(operation.spaceId, operation.listId);
      return;
    case "item.create":
      await api.createItem(operation.spaceId, operation.body);
      await announce(operation, operation.body.id!);
      return;
    case "item.update":
      await api.updateItem(operation.spaceId, operation.itemId, operation.body);
      await announce(operation, operation.itemId);
      return;
    case "item.delete":
      await api.deleteItem(operation.spaceId, operation.itemId);
      return;
    case "wish.create":
      await api.createWish(operation.spaceId, operation.body);
      await announce(operation, operation.body.id!);
      return;
    case "wish.update":
      await api.updateWish(operation.spaceId, operation.wishId, operation.body);
      return;
    case "wish.delete":
      await api.deleteWish(operation.spaceId, operation.wishId);
      return;
    case "place.create":
      await api.createPlace(operation.spaceId, operation.body);
      await announce(operation, operation.body.id!);
      return;
    case "place.update":
      await api.updatePlace(operation.spaceId, operation.placeId, operation.body);
      return;
    case "place.delete":
      await api.deletePlace(operation.spaceId, operation.placeId);
      return;
    case "place.review":
      await api.reviewPlace(operation.spaceId, operation.placeId, operation.body);
      return;
    case "place.unreview":
      await api.unreviewPlace(operation.spaceId, operation.placeId);
      return;
    case "topic.create":
      await api.createTopic(operation.spaceId, operation.body);
      return;
    case "topic.update":
      await api.updateTopic(operation.spaceId, operation.topicId, operation.body);
      return;
    case "topic.delete":
      await api.deleteTopic(operation.spaceId, operation.topicId);
      return;
    case "message.create":
      await api.createMessage(operation.spaceId, operation.body);
      await announce(operation, operation.body.id!);
      return;
    case "message.update":
      await api.updateMessage(operation.spaceId, operation.messageId, operation.body);
      return;
    case "message.delete":
      await api.deleteMessage(operation.spaceId, operation.messageId);
      return;
    case "event.create":
      await api.createEvent(operation.spaceId, operation.body);
      return;
    case "event.update":
      await api.updateEvent(operation.spaceId, operation.eventId, operation.body);
      return;
    case "event.delete":
      await api.deleteEvent(operation.spaceId, operation.eventId);
      return;
    case "message.react":
      await api.react(operation.spaceId, operation.messageId, operation.body);
      return;
    case "event.participate":
      await api.setParticipation(operation.spaceId, operation.eventId, operation.body);
      return;
    case "tag.create":
      await api.createTag(operation.spaceId, operation.body);
      return;
    case "tag.update":
      await api.updateTag(operation.spaceId, operation.tagId, operation.body);
      return;
    case "tag.delete":
      await api.deleteTag(operation.spaceId, operation.tagId);
      return;
    case "tag.link":
      await api.linkTag(operation.spaceId, operation.body);
      return;
    case "tag.unlink":
      await api.unlinkTag(operation.spaceId, operation.body);
      return;
  }
}

/**
 * An error the server will never accept, however many times it is retried.
 * A 404 usually means the row was deleted by the other person while this change
 * was queued, which is a real outcome rather than a fault.
 */
function isPermanent(error: unknown): boolean {
  return error instanceof ApiError && !error.isTransient && !error.isAuthError;
}

/** What became of an operation the caller was waiting on. */
export type Settlement = "sent" | "abandoned";

export class SyncEngine {
  private states = new Map<string, SpaceState>();
  private queue: QueuedOperation[] = readOutbox();
  private listeners = new Set<() => void>();
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private draining = false;
  private watching = false;
  private resumedAt = 0;
  private status: ConnectionStatus = "connecting";
  private abandoned: Operation[] = [];
  private version = 0;
  private snapshot: SyncSnapshot = {
    version: 0,
    status: "connecting",
    pending: 0,
    abandoned: [],
  };
  private self = "";
  private stopped = true;

  /**
   * Callers waiting to hear that a particular operation has actually landed.
   *
   * The reason this exists: the server refuses to attach a file to something it
   * has never heard of, and everything this app creates goes through the outbox
   * first. So "attach a photo to the message I am sending" cannot fire the
   * upload next to the send — it has to wait for the send to arrive. The same
   * hazard already bit tags twice, where a link raced the tag it pointed at and
   * lost permanently, because "not found" is not a transient error.
   */
  private waiting = new Map<string, ((outcome: Settlement) => void)[]>();

  /** Called when the server says the session is gone. */
  onUnauthenticated: (() => void) | null = null;

  /**
   * This space is no longer this person's — deleted, or they were removed.
   *
   * Told apart from an ordinary failure because it never recovers: the server
   * will refuse this space for ever, and a client that treats it as a network
   * blip retries it every few seconds until the tab is closed, showing the
   * contents of a space that does not exist.
   */
  onSpaceGone: ((spaceId: string) => void) | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SyncSnapshot => this.snapshot;

  /**
   * The codec to read and write this space with, or `null` when it is locked.
   *
   * `null` means the space is encrypted and this device does not have the key.
   * Callers must not fall back to plaintext: writing readable content into a
   * space that is supposed to be encrypted is a far worse failure than
   * refusing to write, and it is silent.
   */
  codec = (spaceId: string): Codec | null => {
    const mode = this.states.get(spaceId)?.space?.encryptionMode ?? "none";
    return codecFor(spaceId, mode);
  };

  /**
   * Resolves once this operation has reached the server, or once we have given
   * up on it.
   *
   * Meant to be called immediately after `mutate`, while the operation is still
   * in the queue. An id the queue does not hold is treated as already sent —
   * for a caller that just enqueued it, the only way to be missing is to have
   * drained in between.
   */
  whenSettled = (operationId: string): Promise<Settlement> => {
    if (!this.queue.some((entry) => entry.operation.id === operationId)) {
      return Promise.resolve("sent");
    }

    return new Promise((resolve) => {
      const waiters = this.waiting.get(operationId) ?? [];
      waiters.push(resolve);
      this.waiting.set(operationId, waiters);
    });
  };

  /** Tell whoever asked, and stop holding onto them. */
  private notifySettled(operations: readonly Operation[], outcome: Settlement): void {
    for (const operation of operations) {
      const waiters = this.waiting.get(operation.id);
      if (!waiters) continue;
      this.waiting.delete(operation.id);
      for (const resolve of waiters) resolve(outcome);
    }
  }

  /**
   * The view of a space with unsent changes layered on top. Recomputed on read
   * rather than cached, because both inputs are small and a stale overlay is a
   * far worse bug than a redundant merge.
   */
  view(spaceId: string): SpaceState {
    const base = this.states.get(spaceId) ?? emptySpaceState(spaceId);
    return applyPending(base, this.queue, { self: this.self });
  }

  start(self: string, spaceIds: readonly string[]): void {
    this.self = self;
    this.stopped = false;

    for (const spaceId of spaceIds) {
      if (!this.states.has(spaceId)) this.states.set(spaceId, emptySpaceState(spaceId));
      void this.pull(spaceId);
    }

    this.connect();
    void this.drain();
    this.watch();
  }

  stop(): void {
    this.stopped = true;
    this.socket?.close();
    this.socket = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.unwatch();
    this.states.clear();
    this.emit();
  }

  /**
   * Notice when the app comes back, and catch up.
   *
   * Nothing did, and it is the whole of «обновления приходят, только когда я
   * перезагружаю приложение». A phone that backgrounds a tab does not close its
   * WebSocket so much as freeze it: no `close` fires, so `onclose` never runs,
   * the engine goes on believing it is online, and the reconnect that would
   * have pulled never happens. The app then shows whatever it knew when the
   * screen went off, until it is reloaded.
   *
   * So: on coming back, and on the network returning, pull outright rather than
   * asking whether the socket is well. A pull is one small request and it is
   * correct whatever state the socket is in; deciding that a frozen socket is
   * frozen is the thing nothing can do reliably.
   */
  private watch(): void {
    if (typeof document === "undefined" || this.watching) return;
    this.watching = true;

    document.addEventListener("visibilitychange", this.resume);
    window.addEventListener("online", this.resume);
    window.addEventListener("focus", this.resume);
  }

  private unwatch(): void {
    if (!this.watching) return;
    this.watching = false;

    document.removeEventListener("visibilitychange", this.resume);
    window.removeEventListener("online", this.resume);
    window.removeEventListener("focus", this.resume);
  }

  /**
   * An arrow, so it is the same function object every time and can be removed.
   *
   * Throttled because three listeners fire for one return to the screen — a
   * phone unlocking sends `visibilitychange` and `focus` within a frame of each
   * other — and pulling every space three times says nothing new.
   */
  private resume = (): void => {
    if (this.stopped || document.visibilityState === "hidden") return;

    const now = Date.now();
    if (now - this.resumedAt < RESUME_THROTTLE_MS) return;
    this.resumedAt = now;

    for (const spaceId of this.states.keys()) void this.pull(spaceId);
    void this.drain();

    /*
     * And the socket, if it is not open. `reconnectAttempts` is reset first:
     * after an hour in a pocket the backoff has grown to its ceiling, and
     * waiting a minute to reconnect a screen somebody is looking at right now
     * is the wrong end of that trade.
     */
    if (!this.socket || this.socket.readyState > WebSocket.OPEN) {
      this.reconnectAttempts = 0;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.connect();
    }
  };

  /** Queue a change. It shows immediately and is sent when the network allows. */
  mutate(operation: Operation): void {
    this.queue = enqueue(this.queue, operation);
    writeOutbox(this.queue);
    this.emit();
    void this.drain();
  }

  async pull(spaceId: string): Promise<void> {
    const current = this.states.get(spaceId) ?? emptySpaceState(spaceId);

    try {
      const delta = await api.sync(spaceId, current.revision);

      /*
       * Decrypt before applying, never after.
       *
       * This is the one asynchronous moment in the whole read path, and putting
       * it here is what lets every screen stay synchronous. By the time
       * `applyDelta` returns and listeners are told, everything the next render
       * needs is already readable — so an encrypted space does not flash a
       * screenful of placeholders and then fill in.
       *
       * `restoreSpaceCodec` rather than a cached lookup: the space's mode comes
       * from the delta itself, and on the very first pull after a reload this
       * is where the key stored on the device is picked up.
       */
      const mode = delta.space?.encryptionMode ?? current.space?.encryptionMode ?? "none";
      const codec = await restoreSpaceCodec(spaceId, mode);
      if (codec) await primePlaintext(blobsIn(delta), codec);

      this.states.set(spaceId, applyDelta(current, delta));
      this.setStatus("online");
      this.emit();
    } catch (error) {
      if (error instanceof ApiError && error.isAuthError) {
        this.onUnauthenticated?.();
        return;
      }

      /*
       * 403 and 404 on a space are the same sentence: it is not yours any
       * more. The other person deleted it, or took you out of it — and either
       * way nothing on this device can put it back, so the state is dropped
       * rather than left for a retry that will be refused for ever.
       */
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
        this.states.delete(spaceId);
        this.emit();
        this.onSpaceGone?.(spaceId);
        return;
      }
      if (error instanceof OfflineError) this.setStatus("offline");
      this.emit();
    }
  }

  /* ------------------------------------------------------------- outbox */

  private async drain(): Promise<void> {
    if (this.draining || this.stopped || this.queue.length === 0) return;
    this.draining = true;

    try {
      const inFlight = this.queue;
      const result = await flush(inFlight, send, { isPermanent });

      /**
       * Reconcile rather than assign.
       *
       * `flush` works from the snapshot it was handed, and the person keeps
       * using the app while it is away. Assigning its result back overwrote
       * anything queued in the meantime — silent loss in the one component whose
       * whole job is not to lose work. So: drop what settled, keep the updated
       * attempt counts, and leave everything that arrived since untouched.
       */
      const settled = new Set(
        [...result.sent, ...result.abandoned].map((operation) => operation.id),
      );
      const retried = new Map(
        result.queue.map((entry) => [entry.operation.id, entry] as const),
      );

      this.queue = this.queue
        .filter((entry) => !settled.has(entry.operation.id))
        .map((entry) => retried.get(entry.operation.id) ?? entry);

      writeOutbox(this.queue);

      this.notifySettled(result.sent, "sent");
      this.notifySettled(result.abandoned, "abandoned");

      if (result.abandoned.length > 0) {
        this.abandoned = [...this.abandoned, ...result.abandoned];
      }

      // Anything that landed changed the server's state; pull so this device
      // sees the authoritative row rather than trusting its own overlay.
      const touched = new Set(result.sent.map((operation) => operation.spaceId));
      for (const spaceId of touched) await this.pull(spaceId);

      this.emit();
    } finally {
      this.draining = false;
    }

    // Work queued while this drain was away, or an entry awaiting its backoff.
    if (this.queue.length > 0) this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    const attempts = this.queue[0]?.attempts ?? 1;
    this.flushTimer = setTimeout(() => void this.drain(), backoffMs(attempts));
  }

  /* ---------------------------------------------------------- websocket */

  private connect(): void {
    if (this.stopped || this.socket) return;

    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    let socket: WebSocket;
    try {
      socket = new WebSocket(`${protocol}//${location.host}/api/ws`);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.setStatus("online");
      // A change may have landed while the socket was down, so catch up rather
      // than waiting for the next nudge.
      for (const spaceId of this.states.keys()) void this.pull(spaceId);
      void this.drain();
      this.emit();
    };

    socket.onmessage = (event) => {
      let message;
      try {
        message = serverMessageSchema.safeParse(JSON.parse(String(event.data)));
      } catch {
        return;
      }
      if (!message.success) return;

      if (message.data.type === "changed") {
        const known = this.states.get(message.data.spaceId);
        // Nothing to do if this device is already at or past that revision.
        if (known && known.revision >= message.data.revision) return;
        void this.pull(message.data.spaceId);
      }
    };

    socket.onclose = () => {
      this.socket = null;
      if (this.stopped) return;
      this.setStatus("offline");
      this.emit();
      this.scheduleReconnect();
    };

    socket.onerror = () => socket.close();
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;

    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, backoffMs(this.reconnectAttempts));
  }

  /* ------------------------------------------------------------ plumbing */

  private setStatus(status: ConnectionStatus): void {
    this.status = status;
  }

  private emit(): void {
    // A fresh object every time, with a version that always moves. Reusing the
    // reference when only the synced rows changed is what made deltas invisible.
    this.version += 1;
    this.snapshot = {
      version: this.version,
      status: this.status,
      pending: this.queue.length,
      abandoned: this.abandoned,
    };

    for (const listener of this.listeners) listener();
  }

  acknowledgeAbandoned(): void {
    if (this.abandoned.length === 0) return;
    this.abandoned = [];
    this.emit();
  }
}

export const syncEngine = new SyncEngine();
